import { useEffect, useRef, useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Camera, Loader2, Upload } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

type OcrResponse = {
  value_km: number | null
  unit_detected: 'km' | 'mi' | null
  raw_value: number | null
  confidence: 'high' | 'medium' | 'low'
  photo_r2_key: string
  reason?: string
}

// 2_000_000 km matches the createVehicleSchema cap on the worker. Real-
// world high-mile beaters land around 600-800k km; the cap is just a
// "this is clearly garbage" guard.
const MAX_KM = 2_000_000

export function OdometerPhotoUploadModal({
  vehicleId,
  currentMileage,
  // When this modal is opened from inside an appointment dialog, pass
  // the appointment id so the resulting vehicle_mileage_history row is
  // tied back to the visit. The /vehicles/:id detail page opens the
  // modal without an appointment context — leave undefined there.
  appointmentId,
  open,
  onOpenChange,
}: {
  vehicleId: string
  currentMileage: number | null
  appointmentId?: string | null
  open: boolean
  onOpenChange: (next: boolean) => void
}) {
  const { t } = useTranslation('vehicles')
  const { t: tCommon } = useTranslation('common')
  const { getToken } = useAuth()
  const queryClient = useQueryClient()

  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [file, setFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [dragActive, setDragActive] = useState(false)

  const [ocr, setOcr] = useState<OcrResponse | null>(null)
  const [ocrError, setOcrError] = useState<string | null>(null)

  // Mechanic-editable mileage in km. Pre-filled by OCR; user can
  // correct on any confidence. Free-form string so the user can clear
  // and retype without fighting a number-input's quirks.
  const [mileage, setMileage] = useState('')
  const [submitError, setSubmitError] = useState<string | null>(null)

  // Reset whenever the dialog closes — never leave stale state behind.
  // Pattern A (You Might Not Need an Effect): snapshot the previous
  // `open` and in-render setState when the dialog transitions closed.
  const [prevOpen, setPrevOpen] = useState(open)
  if (open !== prevOpen) {
    setPrevOpen(open)
    if (!open) {
      setFile(null)
      setPreviewUrl(null)
      setDragActive(false)
      setOcr(null)
      setOcrError(null)
      setMileage('')
      setSubmitError(null)
    }
  }

  // Preview blob URL lifecycle — paired createObjectURL / revoke is a
  // genuine browser-resource lifecycle that must live in an effect. The
  // null branch is a derived-in-render reset (Pattern A) so the effect
  // only runs when a file actually exists.
  const [prevFile, setPrevFile] = useState<File | null>(null)
  if (file !== prevFile) {
    setPrevFile(file)
    if (!file && previewUrl !== null) setPreviewUrl(null)
  }
  useEffect(() => {
    if (!file) return
    const url = URL.createObjectURL(file)
    // eslint-disable-next-line react-hooks/set-state-in-effect -- paired with revokeObjectURL cleanup; genuine browser-resource lifecycle
    setPreviewUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [file])

  const ocrMutation = useMutation({
    mutationFn: async (photo: File) => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))

      const fd = new FormData()
      fd.append('photo', photo)

      const res = await fetch(`/api/vehicles/${vehicleId}/odometer-photo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(
          text || tCommon('error.request_failed', { status: res.status }),
        )
      }
      return (await res.json()) as OcrResponse
    },
    onSuccess: (data) => {
      setOcr(data)
      setOcrError(null)
      if (data.value_km !== null) setMileage(String(data.value_km))
    },
    onError: (err) => {
      setOcr(null)
      setOcrError(
        err instanceof Error
          ? err.message
          : t('odometer_modal.error.ocr_failed'),
      )
    },
  })

  const saveMutation = useMutation({
    mutationFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))

      const value = Number(mileage)
      // Tag the resulting vehicle_mileage_history row as a photo
      // capture and pass the R2 key so the history panel can link
      // back to the source image. When the OCR step failed but the
      // mechanic kept the typed value, fall back to source='manual'
      // (no R2 key means no photo provenance to claim).
      const isPhotoCapture = ocr?.photo_r2_key != null
      const res = await fetch(`/api/vehicles/${vehicleId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          mileage: value,
          mileage_source: isPhotoCapture ? 'photo' : 'manual',
          mileage_photo_r2_key: isPhotoCapture ? ocr?.photo_r2_key : null,
          mileage_appointment_id: appointmentId ?? null,
        }),
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(
          text || tCommon('error.request_failed', { status: res.status }),
        )
      }
      return (await res.json()) as { ok: true; id: string }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicle', vehicleId] })
      queryClient.invalidateQueries({
        queryKey: ['vehicle', vehicleId, 'mileage-history'],
      })
      queryClient.invalidateQueries({ queryKey: ['vehicles'] })
      // When opened from the appointment dialog the mileage is shown
      // in the vehicle summary card — refetch so the new value appears
      // without closing/reopening the dialog.
      queryClient.invalidateQueries({ queryKey: ['appointments'] })
      onOpenChange(false)
    },
    onError: (err) => {
      setSubmitError(
        err instanceof Error
          ? err.message
          : t('odometer_modal.error.save_failed'),
      )
    },
  })

  function handleFileSelected(f: File | null) {
    setFile(f)
    setOcr(null)
    setOcrError(null)
    setMileage('')
    setSubmitError(null)
    if (f) ocrMutation.mutate(f)
  }

  function onDrop(e: React.DragEvent<HTMLLabelElement>) {
    e.preventDefault()
    setDragActive(false)
    const f = e.dataTransfer.files?.[0] ?? null
    if (f && f.type.startsWith('image/')) handleFileSelected(f)
  }

  const trimmed = mileage.trim()
  const parsed = trimmed ? Number(trimmed.replace(/[\s,]/g, '')) : NaN
  const validMileage =
    Number.isFinite(parsed) && Number.isInteger(parsed) && parsed >= 0 && parsed <= MAX_KM
  const submitting = saveMutation.isPending
  const canSave = validMileage && !submitting

  // Soft hint when the mechanic confirms a value lower than what's
  // already on the row — usually means the photo is from a prior
  // service or the OCR misread digits. We don't block (the mechanic
  // might be correcting a bad stored value), just nudge.
  const wouldDecrease =
    validMileage && currentMileage !== null && parsed < currentMileage

  function handleSave(e: React.FormEvent) {
    e.preventDefault()
    setSubmitError(null)
    if (!validMileage) {
      setSubmitError(t('odometer_modal.error.invalid_value'))
      return
    }
    saveMutation.mutate()
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => (!submitting ? onOpenChange(next) : null)}
    >
      <DialogContent className="sm:max-w-xl max-h-[calc(100dvh-2rem)] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('odometer_modal.title')}</DialogTitle>
          <DialogDescription>
            {t('odometer_modal.subtitle')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSave} className="space-y-5">
          {/* ─── Upload ────────────────────────────────────────────── */}
          <section className="space-y-2">
            <div className="text-[13px] font-semibold text-muted-foreground">
              {t('odometer_modal.upload.header')}
            </div>

            {previewUrl ? (
              <div className="space-y-3">
                <img
                  src={previewUrl}
                  alt={t('odometer_modal.upload.preview_alt')}
                  className="max-h-56 w-full rounded-[10px] border border-border object-contain"
                />
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={submitting}
                  >
                    <Upload className="size-4" />
                    {t('from_photo.upload.replace')}
                  </Button>
                </div>
              </div>
            ) : (
              <label
                htmlFor={`odometer-photo-modal-${vehicleId}`}
                onClick={(e) => {
                  // Same iOS Safari workaround as the VIN modal — sr-only
                  // file inputs sometimes drop the implicit label→click
                  // bridge. Explicit .click() makes the picker reliable.
                  e.preventDefault()
                  fileInputRef.current?.click()
                }}
                onDragOver={(e) => {
                  e.preventDefault()
                  setDragActive(true)
                }}
                onDragLeave={() => setDragActive(false)}
                onDrop={onDrop}
                className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-[10px] border-2 border-dashed bg-muted/30 px-4 py-6 text-center transition-[background-color,border-color] ${
                  dragActive
                    ? 'border-foreground/40 bg-muted'
                    : 'border-border [@media(hover:hover)_and_(pointer:fine)]:hover:border-foreground/30 [@media(hover:hover)_and_(pointer:fine)]:hover:bg-muted/60'
                }`}
              >
                <Camera className="size-7 text-ink-3" />
                <div className="text-sm font-medium text-foreground">
                  {t('odometer_modal.upload.tap_or_drop')}
                </div>
                <div className="text-xs text-muted-foreground">
                  {t('from_photo.upload.formats')}
                </div>
              </label>
            )}

            <input
              ref={fileInputRef}
              id={`odometer-photo-modal-${vehicleId}`}
              type="file"
              accept="image/*"
              className="sr-only"
              onChange={(e) => {
                const f = e.target.files?.[0] ?? null
                handleFileSelected(f)
                e.target.value = ''
              }}
            />

            {ocrMutation.isPending ? (
              <div className="rounded-[10px] border border-border bg-muted p-2.5 text-xs text-muted-foreground">
                {t('odometer_modal.extracting')}
              </div>
            ) : null}

            {ocrError ? (
              <div className="rounded-[10px] border border-destructive/30 bg-destructive/10 p-2.5 text-xs text-destructive">
                {ocrError}
              </div>
            ) : null}

            {ocr && ocr.value_km === null ? (
              <div className="rounded-[10px] border border-attention-line bg-attention-soft p-2.5 text-xs text-attention-text">
                {t('odometer_modal.no_value_detected')}
              </div>
            ) : null}

            {ocr && ocr.value_km !== null && ocr.confidence !== 'high' ? (
              <div className="rounded-[10px] border border-attention-line bg-attention-soft p-2.5 text-xs text-attention-text">
                {t('odometer_modal.confidence_warning', {
                  confidence: tCommon(`confidence.${ocr.confidence}`),
                })}
              </div>
            ) : null}

            {ocr && ocr.value_km !== null && ocr.unit_detected === 'mi' ? (
              <div className="rounded-[10px] border border-border bg-muted/40 p-2.5 text-xs text-muted-foreground">
                {t('odometer_modal.converted_from_miles', {
                  raw: ocr.raw_value?.toLocaleString() ?? '',
                  km: ocr.value_km.toLocaleString(),
                })}
              </div>
            ) : null}
          </section>

          {/* ─── Review ──────────────────────────────────────────── */}
          {ocr || mileage ? (
            <section className="space-y-2">
              <div className="text-[13px] font-semibold text-muted-foreground">
                {t('odometer_modal.review.header')}
              </div>
              <Label htmlFor={`odometer-mileage-${vehicleId}`}>
                {t('odometer_modal.review.label')}
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id={`odometer-mileage-${vehicleId}`}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={mileage}
                  onChange={(e) =>
                    setMileage(e.target.value.replace(/[^\d]/g, ''))
                  }
                  placeholder={t('odometer_modal.review.placeholder')}
                  maxLength={8}
                  disabled={submitting}
                  className="font-mono sm:w-44"
                />
                <span className="text-sm text-muted-foreground">
                  {t('odometer_modal.review.km_suffix')}
                </span>
              </div>
              {currentMileage !== null ? (
                <p className="text-xs text-muted-foreground">
                  {t('odometer_modal.review.current', {
                    value: currentMileage.toLocaleString(),
                  })}
                </p>
              ) : null}
              {wouldDecrease ? (
                <p className="text-xs text-attention-text">
                  {t('odometer_modal.review.decrease_warning')}
                </p>
              ) : null}
            </section>
          ) : null}

          {submitError ? (
            <div className="rounded-[10px] border border-destructive/30 bg-destructive/10 p-2.5 text-xs text-destructive">
              {submitError}
            </div>
          ) : null}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              {t('odometer_modal.cancel')}
            </Button>
            <Button type="submit" disabled={!canSave}>
              {submitting ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  {t('odometer_modal.saving')}
                </>
              ) : (
                t('odometer_modal.save')
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
